import { useRef, useState } from 'react';
import { FEATURE_FLAGS } from '@/constants';
import { AUDIO_FALLBACK_SAMPLE_RATE } from '../constants';
import { decodeBase64Pcm16ToFloat32, encodePcm16ToBase64 } from '../utils';

export interface UseVoiceLiveAudioResult {
  /** Analyser tapping the user's mic (input meter); null until capture / flag off. */
  inputAnalyser: AnalyserNode | null;
  /** Analyser tapping the avatar/fallback audio (output meter). */
  outputAnalyser: AnalyserNode | null;
  /** Create the shared 24kHz AudioContext for a new session. */
  createContext: () => AudioContext;
  /** Close and discard the AudioContext (if still open). */
  closeContext: () => void;
  /** Start mic capture; PCM16 chunks are delivered base64-encoded to onChunk. */
  startCapture: (onChunk: (base64Audio: string) => void) => Promise<void>;
  /** Stop mic capture (media stream + worklet). */
  stopCapture: () => void;
  /** Tap an incoming WebRTC audio MediaStream for the output meter (flag-gated). */
  tapOutputFromStream: (stream: MediaStream) => void;
  /** Play a base64 PCM16 chunk (audio-only fallback), scheduled gaplessly. */
  playDelta: (base64Audio: string) => void;
  /** Stop and discard any in-flight fallback playback (barge-in / cleanup). */
  stopPlayback: () => void;
  /** Disconnect and clear the input/output meter analysers. */
  teardownAnalysers: () => void;
}

/**
 * Owns the client-side audio subsystem for the Voice Live session: the shared
 * 24kHz AudioContext, microphone capture (via an AudioWorklet), the
 * input/output meter analysers (gated by ENABLE_AUDIO_IO_VISUALIZER), and the
 * audio-only fallback playback path (gapless PCM16 scheduling over the
 * WebSocket). All Web Audio graph state lives here; the connection machine
 * drives it through the returned methods.
 */
export const useVoiceLiveAudio = (): UseVoiceLiveAudioResult => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);

  // Web Audio playback state for audio-only fallback (PCM16 over WebSocket).
  const playbackCursorRef = useRef<number>(0);
  const activeAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  // Web Audio analysers feeding the input/output visualizer. The refs are the
  // source of truth for the audio graph and teardown; the state mirrors are
  // what the meter consumes.
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const [inputAnalyser, setInputAnalyser] = useState<AnalyserNode | null>(null);
  const [outputAnalyser, setOutputAnalyser] = useState<AnalyserNode | null>(null);

  /**
   * Stop and discard any in-flight fallback audio playback sources and reset
   * the scheduling cursor. Used on barge-in (user interruption) and cleanup.
   */
  const stopPlayback = () => {
    activeAudioSourcesRef.current.forEach((source) => {
      try {
        source.onended = null;
        source.stop();
      } catch {
        // Source may already have stopped — ignore.
      }
    });
    activeAudioSourcesRef.current.clear();
    playbackCursorRef.current = 0;
  };

  /**
   * Disconnect and clear the input/output meter analysers. Safe to call when
   * the audio context is already closed (disconnect errors are ignored).
   */
  const teardownAnalysers = () => {
    [inputAnalyserRef, outputAnalyserRef].forEach((ref) => {
      if (ref.current) {
        try {
          ref.current.disconnect();
        } catch {
          // Context may already be closed — ignore.
        }
        ref.current = null;
      }
    });
    setInputAnalyser(null);
    setOutputAnalyser(null);
  };

  /**
   * Lazily create the shared output analyser for the audio-only fallback
   * playback path, inserted between the PCM buffer sources and the speakers so
   * the output meter reflects the avatar's speech. Returns null when the
   * visualizer flag is off so playback connects straight to the destination.
   */
  const ensureFallbackOutputAnalyser = (ctx: AudioContext): AnalyserNode | null => {
    if (!FEATURE_FLAGS.ENABLE_AUDIO_IO_VISUALIZER) {
      return null;
    }
    if (outputAnalyserRef.current) {
      return outputAnalyserRef.current;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    analyser.connect(ctx.destination);
    outputAnalyserRef.current = analyser;
    setOutputAnalyser(analyser);
    return analyser;
  };

  /**
   * Decode a base64 PCM16 chunk from `response.audio.delta` and schedule it for
   * gapless playback through the shared AudioContext. Only used in audio-only
   * fallback mode — in WebRTC mode the avatar audio arrives on the media track.
   */
  const playDelta = (base64Audio: string) => {
    const audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state === 'closed') {
      return;
    }
    // Resume if the context was suspended (e.g. autoplay policy).
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        /* best-effort */
      });
    }

    const float32 = decodeBase64Pcm16ToFloat32(base64Audio);
    if (!float32) return;

    const audioBuffer = audioContext.createBuffer(1, float32.length, AUDIO_FALLBACK_SAMPLE_RATE);
    audioBuffer.getChannelData(0).set(float32);

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    // Route through the output analyser (for the meter) when enabled; otherwise
    // connect straight to the speakers as before.
    const outNode = ensureFallbackOutputAnalyser(audioContext);
    source.connect(outNode ?? audioContext.destination);

    // Schedule gaplessly — never start in the past.
    const now = audioContext.currentTime;
    const startAt = Math.max(now, playbackCursorRef.current);
    source.start(startAt);
    playbackCursorRef.current = startAt + audioBuffer.duration;

    activeAudioSourcesRef.current.add(source);
    source.onended = () => {
      activeAudioSourcesRef.current.delete(source);
    };
  };

  /** Create the shared 24kHz AudioContext for a new session. */
  const createContext = (): AudioContext => {
    const audioContext = new AudioContext({ sampleRate: 24000 });
    audioContextRef.current = audioContext;
    return audioContext;
  };

  /** Close and discard the AudioContext (if still open). */
  const closeContext = () => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  /** Stop microphone capture: stop the media stream and disconnect the worklet. */
  const stopCapture = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }
  };

  /**
   * Start microphone capture. Requests mic access, taps the input meter (when
   * enabled), and streams PCM16 chunks (base64-encoded) to `onChunk` via an
   * AudioWorklet. The worklet is intentionally not connected to the
   * destination, avoiding echo.
   */
  const startCapture = async (onChunk: (base64Audio: string) => void): Promise<void> => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    // The context can be closed or replaced by teardown (Stop, unmount,
    // reconnect, or a superseded WebSocket) while we await getUserMedia and
    // addModule below. Constructing nodes against a closed context throws
    // "AudioWorkletNode cannot be created: No execution context available", so
    // re-check after each await and abort quietly if the context went stale.
    const isContextStale = () =>
      audioContextRef.current !== audioContext || audioContext.state === 'closed';

    // Request microphone access
    // Note: Browser may not support exact sampleRate, it will resample automatically
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true, // Enable browser echo cancellation for first line of defense
        noiseSuppression: false, // Disable browser noise suppression (using Azure's)
        autoGainControl: false, // Let Azure handle this
        sampleRate: 24000, // Request 24kHz to match backend
      },
    });

    // Teardown ran while we waited for mic permission — release the freshly
    // acquired stream and bail without touching the dead context.
    if (isContextStale()) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    mediaStreamRef.current = stream;

    // Create audio source from microphone
    const source = audioContext.createMediaStreamSource(stream);

    // Tap the mic for the input meter (read-only branch — the worklet is not
    // connected to the destination, so this adds no echo). Gated by the flag.
    if (FEATURE_FLAGS.ENABLE_AUDIO_IO_VISUALIZER) {
      try {
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        inputAnalyserRef.current = analyser;
        setInputAnalyser(analyser);
      } catch (err) {
        console.warn('Failed to set up input audio meter:', err);
      }
    }

    // Load and use AudioWorklet for better performance (runs on audio thread)
    try {
      await audioContext.audioWorklet.addModule('/audio-capture-processor.js');

      // Teardown may have closed the context while the module loaded — abort
      // quietly instead of throwing from the AudioWorkletNode constructor.
      if (isContextStale()) {
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        return;
      }

      const workletNode = new AudioWorkletNode(audioContext, 'audio-capture-processor');
      audioWorkletNodeRef.current = workletNode;

      // Handle audio data from worklet
      workletNode.port.onmessage = (event) => {
        if (event.data.type === 'audio') {
          const base64Audio = encodePcm16ToBase64(event.data.data);
          onChunk(base64Audio);
        }
      };

      // Connect nodes
      source.connect(workletNode);
      // Don't connect worklet to destination to avoid echo
    } catch (error) {
      // A late teardown can still race the constructor; if the context is now
      // stale, treat it as a benign abort rather than a microphone failure so
      // the caller's retry loop doesn't surface a spurious mic error.
      if (isContextStale()) {
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        return;
      }
      console.error('Failed to initialize AudioWorklet:', error);
      throw error;
    }

    console.log('Audio capture started');
  };

  /**
   * Tap an incoming WebRTC audio MediaStream for the output meter (gated by the
   * flag). A zero-gain sink keeps audio flowing through the analyser so it
   * produces data, while audible playback stays on the caller's <audio>
   * element — the sink is silent, so there is no double audio.
   */
  const tapOutputFromStream = (stream: MediaStream) => {
    if (FEATURE_FLAGS.ENABLE_AUDIO_IO_VISUALIZER && audioContextRef.current) {
      try {
        const ctx = audioContextRef.current;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        const streamSource = ctx.createMediaStreamSource(stream);
        const silentSink = ctx.createGain();
        silentSink.gain.value = 0;
        streamSource.connect(analyser);
        analyser.connect(silentSink);
        silentSink.connect(ctx.destination);
        outputAnalyserRef.current = analyser;
        setOutputAnalyser(analyser);
      } catch (err) {
        console.warn('Failed to set up output audio meter:', err);
      }
    }
  };

  return {
    inputAnalyser,
    outputAnalyser,
    createContext,
    closeContext,
    startCapture,
    stopCapture,
    tapOutputFromStream,
    playDelta,
    stopPlayback,
    teardownAnalysers,
  };
};
